import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { Globals } from '../../globals';
import {
  AddSipDeviceToNewInstallationRequest,
  DoorbellCallEntry,
  LinkQsdDoorbellRequest,
  LinkQsdDoorbellToNewInstallationRequest,
  SipDevice,
  SipFeedback,
} from './sip-devices.interfaces';

@Injectable({
  providedIn: 'root',
})
export class SipDevicesService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);

  /**
   * Get all sip devices by type for the currently logged in user
   */
  getSipDevicesByType(typeId: number): Observable<SipDevice[]> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/type/${typeId}`;
    return this.http.get<SipDevice[]>(url);
  }

  /**
   * Get sip devices by type that are shared with the currently logged in user
   */
  getSharedSipDevicesByType(typeId: number): Observable<SipDevice[]> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/type/${typeId}/shared`;
    return this.http.get<SipDevice[]>(url);
  }

  /**
   * Get all sip devices by type for the currently logged in user
   */
  getSipDevicesByTypeWithLink(
    typeId: number,
    parentOrChildId: number,
  ): Observable<SipDevice[]> {
    const url = `${this.globals.apiRoot}/v2/jwtsipdevices/type/${typeId}/link/${parentOrChildId}`;
    return this.http.get<SipDevice[]>(url);
  }

  /**
   * Get all sip devices and child sip devices by type for the currently logged in user
   */
  getSipDevicesWithChildrenByType(typeId: number): Observable<SipDevice[]> {
    const url = `${this.globals.apiRoot}/v2/jwtsipdevices/type/${typeId}/children`;
    return this.http.get<SipDevice[]>(url);
  }

  /**
   * get a count of sip devices for the currently logged in user
   */
  getSipDevicesCount(): Observable<number> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/count`;
    return this.http.get<number>(url);
  }

  /**
   * Verify if the user owns sipdevices of a specific type
   */
  sipDevicesByTypeExist(typeId: number): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/type/${typeId}/exists`;
    return this.http.get<boolean>(url);
  }

  /**
   * Verify if the sip device exists
   */
  sipDeviceByUidExists(uid: string): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/${uid}/exists`;
    return this.http.get<boolean>(url);
  }

  /**
   * Check whether a SIP account belongs to the currently logged-in user.
   */
  sipAccountOwned(sipAccount: string): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/sipAccountOwned`;
    return this.http
      .get<{ owned: boolean }>(url, { params: { sipAccount } })
      .pipe(map((response) => response.owned));
  }

  /**
   * Get sip device by id
   * @param id Sip device id
   */
  getSipDevice(id: number): Observable<SipDevice> {
    const url = `${this.globals.apiRoot}/v2/jwtsipdevices/${id}`;
    return this.http.get<SipDevice>(url);
  }

  /**
   * Get sip device by extension
   * --> used to display correct sip device name and functionblock to click
   * @param extension Sip device extension
   */
  getSipDeviceByExtension(extension: string): Observable<SipDevice> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/${extension}/ext`;
    return this.http.get<SipDevice>(url);
  }

  /**
   * Get sip device config by uid
   * @param uid Sip device uid
   */
  getSipDeviceConfig(uid: string): Observable<SipDevice> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/${uid}/config`;
    return this.http.get<SipDevice>(url);
  }

  /**
   * Add a sip device
   * @param sipDevice the sip device to add
   */
  addSipDevice(device: SipDevice): Observable<SipDevice> {
    const url = `${this.globals.apiRoot}/jwtsipdevices`;
    return this.http.post<SipDevice>(url, device);
  }

  /**
   * Add a sip device to a new installation (atomically)
   * @param request the sip device and installation data
   */
  addSipDeviceToNewInstallation(
    request: AddSipDeviceToNewInstallationRequest,
  ): Observable<SipDevice> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/installation`;
    return this.http.post<SipDevice>(url, request);
  }

  /**
   * Add a sip device to an existing installation
   * @param installationId the installation id
   * @param device the sip device to add
   */
  addSipDeviceToExistingInstallation(
    installationId: number,
    device: SipDevice,
  ): Observable<SipDevice> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/installation/${installationId}`;
    return this.http.post<SipDevice>(url, device);
  }

  /**
   * Link a discovered QSD doorbell (controller) to an installation.
   * The controller record must already exist (created by the QSD registration flow).
   */
  linkQsdDoorbell(
    request: LinkQsdDoorbellRequest,
  ): Observable<{ controllerId: number }> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/linkQsdDoorbell`;
    return this.http.post<{ controllerId: number }>(url, request);
  }

  /**
   * Create a new installation and link a discovered QSD doorbell to it (atomic).
   * Rolls back the installation on failure.
   */
  linkQsdDoorbellToNewInstallation(
    request: LinkQsdDoorbellToNewInstallationRequest,
  ): Observable<{ controllerId: number }> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/installation/linkQsdDoorbell`;
    return this.http.post<{ controllerId: number }>(url, request);
  }

  /**
   * update a sipdevice
   * @param sipDevice The sip device you want to update with the properties as you need them to be
   */
  updateSipDevice(sipDevice: SipDevice): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/${sipDevice.id}`;
    return this.http.put<void>(url, sipDevice);
  }

  /**
   * Remove a sip doorbell device
   * @param sipDeviceId the device id
   * @returns return a boolean if it succeeded or not
   */
  deleteSipDoorbell(sipDeviceId: number): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/${sipDeviceId}/1`;
    return this.http.delete<boolean>(url);
  }

  /**
   * Remove a sip phone device
   * @param sipDeviceId the device id
   * @returns return a boolean if it succeeded or not
   */
  deleteSipPhone(sipDeviceId: number): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/${sipDeviceId}/2`;
    return this.http.delete<boolean>(url);
  }

  /**
   * Link sip device to another sip device
   * @param id  id
   * @param sipDeviceId id of the sip device to link
   */
  addSipDevicesLink(id: number, sipDeviceId: number): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/${id}/link/${sipDeviceId}`;
    return this.http.post<boolean>(url, sipDeviceId);
  }

  addSipDevicesLinkShared(
    id: number,
    sipDeviceId: number,
  ): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/${id}/link/${sipDeviceId}/shared`;
    return this.http.post<boolean>(url, sipDeviceId);
  }

  /**
   * Delete link between sip devices
   * @param parentSipDeviceId id of the sip device parent
   * @param childSipDeviceId id of the sip device child
   */
  deleteSipDevicesLink(
    parentSipDeviceId: number,
    childSipDeviceId: number,
  ): Observable<SipFeedback> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/${parentSipDeviceId}/link/${childSipDeviceId}`;
    return this.http.delete<SipFeedback>(url);
  }

  /**
   * Delete link between sip devices shared
   * @param parentSipDeviceId id of the sip device parent
   * @param childSipDeviceId id of the sip device child
   */
  deleteSipDevicesLinkShared(
    parentSipDeviceId: number,
    childSipDeviceId: number,
  ): Observable<SipFeedback> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/${parentSipDeviceId}/link/${childSipDeviceId}/shared`;
    return this.http.delete<SipFeedback>(url);
  }

  /**
   * Check the license code for the sipdevice
   */
  checkSipLicenseCode(code: any): Observable<number> {
    const url = `${this.globals.apiRoot}/siplicense/exists`;
    return this.http.post<number>(url, code);
  }

  /**
   * Get grouped doorbell call entries (paired jpg+mp4) with thumbnail presigned URLs.
   * @param doorbellSerial Doorbell controller serial number (e.g. QSD_57)
   */
  getDoorbellCallEntries(
    doorbellSerial: string,
  ): Observable<DoorbellCallEntry[]> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/s3/objects`;
    return this.http.get<DoorbellCallEntry[]>(url, {
      params: { doorbellSerial },
    });
  }

  /**
   * Get a presigned URL for a video (mp4) S3 object.
   * @param doorbellSerial Doorbell serial (e.g. QSD_57)
   * @param videoFile Full video filename including extension (e.g. 1778581517.mp4)
   */
  getDoorbellCallPresignedVideoUrl(
    doorbellSerial: string,
    videoFile: string,
  ): Observable<string> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/s3/presignedvideourl`;
    return this.http
      .get<{ url: string }>(url, { params: { doorbellSerial, videoFile } })
      .pipe(map((r) => r.url));
  }

  deleteDoorbellCallEntry(
    doorbellSerial: string,
    videoFile: string,
  ): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtsipdevices/s3/entry`;
    return this.http.delete<void>(url, {
      params: { doorbellSerial, videoFile },
    });
  }
}
